import { Box, CircularProgress } from '@mui/material';
import { useMemo, useState } from 'react';
import { KitButton, KitCard, KitList, KitPill, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitBanner,
    KitDropzone,
    KitField,
    KitFormBar,
    KitInput,
    KitKV,
    KitSearch,
    KitSwitch,
    KitWizard,
} from '../common/MobileKitExtras';

/**
 * Importar NF-e no shell mobile — `prototipos-mobile/compras/importar.html`.
 * Passo 1 = envio dos arquivos (protótipo); passos 2/3 (conferir/lançar) derivam do mesmo kit.
 * Só apresentação: leitura, vínculo e lançamento vêm de `pages/purchases/Import.js`.
 */

const UploadIcon = protoIcon('upload');
const InfoIcon = protoIcon('info');
const PackageIcon = protoIcon('package');
const XIcon = protoIcon('x');
const FileIcon = protoIcon('file');
const TruckIcon = protoIcon('truck');

const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const formatDate = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

const productLabel = (p) => (p.code ? `${p.code} - ${p.name}` : p.name || '');

function Check({ checked, disabled, indeterminate, onChange, label }) {
    return (
        <Box component="label" sx={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, m: '-10px 0 -10px -10px', opacity: disabled ? 0.4 : 1 }}>
            <Box
                component="input"
                type="checkbox"
                aria-label={label}
                checked={checked}
                disabled={disabled}
                ref={(el) => { if (el) el.indeterminate = Boolean(indeterminate); }}
                onChange={onChange}
                sx={{ width: 22, height: 22, m: 0, accentColor: (t) => t.palette.primary.main, colorScheme: (t) => t.palette.mode }}
            />
        </Box>
    );
}

function ItemStatus({ item }) {
    if (item.done) return <KitPill tone="success">{item.result || 'Lançado'}</KitPill>;
    if (item.already_imported) return <KitPill>Já lançado nesta nota</KitPill>;
    if (item.result) return <KitPill tone="danger">Não lançado</KitPill>;
    return item.linked ? <KitPill tone="info">Atualiza estoque</KitPill> : <KitPill tone="purple">Criar produto</KitPill>;
}

function FileRow({ file, onRemove, canRemove }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '8px 12px', border: '1px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: 'background.subtle', minHeight: 44 }}>
            <Box sx={{ color: 'primary.main', display: 'flex' }}><FileIcon size={18} /></Box>
            <Box sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</Box>
            <Box sx={{ fontSize: 11.5, color: 'text.disabled', flexShrink: 0 }}>{(file.size / 1024).toFixed(1)} KB</Box>
            {canRemove && (
                <Box component="button" type="button" aria-label="Remover arquivo" onClick={onRemove} sx={{ width: 44, height: 44, m: '-8px -12px -8px 0', border: 0, background: 'none', color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <XIcon size={16} />
                </Box>
            )}
        </Box>
    );
}

export default function MobilePurchaseImport({
    fileInputRef,
    accept,
    maxFiles,
    maxSizeMb,
    files,
    parsing,
    onAddFiles,
    onRemoveFile,
    onParse,
    invoices,
    products,
    updateCost,
    onUpdateCost,
    dueDate,
    onDueDate,
    isSelectable,
    selectedCount,
    selectableCount,
    creatableCount,
    updatableCount,
    submitting,
    onToggleAll,
    onToggleInvoice,
    onUpdateItem,
    onCreate,
    onUpdate,
    supplierLabel,
    formatDocument,
}) {
    const [pickerFor, setPickerFor] = useState(null); // { uid, key }
    const [pickerQuery, setPickerQuery] = useState('');
    const hasInvoices = invoices.length > 0;
    const hasResult = invoices.some((inv) => inv.result);

    const pickerResults = useMemo(() => {
        const q = norm(pickerQuery.trim());
        const list = q ? products.filter((p) => norm(p.name).includes(q) || norm(p.code).includes(q)) : products;
        return list.slice(0, 50);
    }, [products, pickerQuery]);

    const pickProduct = (p) => {
        onUpdateItem(pickerFor.uid, pickerFor.key, { linked: p ? { id: p.id, code: p.code, name: p.name } : null });
        setPickerFor(null);
        setPickerQuery('');
    };

    const step = !hasInvoices ? 1 : hasResult ? 3 : 2;
    const stepLabel = ['Enviar arquivo', 'Conferir itens', 'Resultado'][step - 1];
    const allSelected = selectedCount === selectableCount && selectableCount > 0;

    const fileInput = (
        <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple
            hidden
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onAddFiles(e.target.files)}
        />
    );

    const fileList = files.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '12px' }}>
            {files.map((f, i) => <FileRow key={`${f.name}:${f.size}`} file={f} canRemove={!parsing} onRemove={() => onRemoveFile(i)} />)}
        </Box>
    );

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitWizard step={step} total={3} label={`Passo ${step} de 3 · ${stepLabel}`} />

            {!hasInvoices && (
                <>
                    <KitDropzone icon={UploadIcon} iconSize={32} height={160} onClick={() => fileInputRef.current?.click()}>
                        <b>Toque para escolher o arquivo</b>
                        <Box component="span" sx={{ fontSize: 11.5 }}>.xml ou .pdf · até {maxSizeMb} MB · máx. {maxFiles} arquivos</Box>
                    </KitDropzone>
                    {fileInput}
                    {fileList}

                    <SecH title="Colunas esperadas" />
                    <KitCard>
                        <KitKV label="Arquivo" value="XML da NF-e ou PDF" />
                        <KitKV label="Fornecedor" value="Detectado pelo CNPJ" />
                        <KitKV label="Itens" value="Casados pelo código" />
                    </KitCard>

                    <Box sx={{ mt: '12px' }}>
                        <KitBanner icon={InfoIcon} iconSize={18} title="Dica">
                            Itens sem correspondência podem ser cadastrados na próxima etapa.
                        </KitBanner>
                    </Box>

                    <KitFormBar>
                        <KitButton variant="primary" disabled={!files.length || parsing} onClick={onParse}>
                            {parsing ? <><CircularProgress size={16} thickness={5} color="inherit" /> Lendo notas...</> : 'Continuar'}
                        </KitButton>
                    </KitFormBar>
                </>
            )}

            {hasInvoices && (
                <>
                    <KitCard>
                        <Box sx={{ fontWeight: 700, fontSize: 14.5 }}>{selectedCount} de {selectableCount} item(ns) selecionado(s)</Box>
                        <KitSwitch
                            title="Atualizar custo"
                            description="Atualiza o custo dos produtos existentes com o valor da nota"
                            checked={updateCost}
                            onChange={(e) => onUpdateCost(e.target.checked)}
                        />
                        <KitField label="Vencimento" help="Conta a pagar gerada para cada nota" sx={{ mb: '12px' }}>
                            <KitInput type="date" value={dueDate} onChange={(e) => onDueDate(e.target.value)} aria-label="Vencimento" />
                        </KitField>
                        <KitButton variant="soft" block size="sm" disabled={!selectableCount} onClick={() => onToggleAll(!allSelected)}>
                            {allSelected ? 'Limpar seleção' : 'Selecionar todos'}
                        </KitButton>
                    </KitCard>

                    {invoices.map((inv) => {
                        if (inv.error) {
                            return (
                                <Box key={inv.uid} sx={{ mt: '12px' }}>
                                    <KitBanner tone="danger" title={inv.file_name}>{inv.error}</KitBanner>
                                </Box>
                            );
                        }
                        const selectable = inv.items.filter(isSelectable);
                        const sel = selectable.filter((it) => it.selected).length;
                        return (
                            <Box key={inv.uid}>
                                <SecH title={`NF-e ${inv.number || '—'}${inv.series ? ` / série ${inv.series}` : ''}`} />
                                <KitCard sx={{ p: '14px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, fontWeight: 700 }}>
                                            <Box sx={{ display: 'flex', color: 'text.disabled' }}><TruckIcon size={16} /></Box>
                                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{supplierLabel(inv.supplier)}</Box>
                                        </Box>
                                        <KitPill tone={inv.supplier_exists ? 'success' : 'warning'}>{inv.supplier_exists ? 'Cadastrado' : 'Não cadastrado'}</KitPill>
                                    </Box>
                                    {inv.supplier.document && <KitKV label="CNPJ/CPF" value={formatDocument(inv.supplier.document)} />}
                                    <KitKV label="Emissão" value={formatDate(inv.issue_date)} />
                                    <KitKV label="Arquivo" value={`${inv.file_name} (${inv.source === 'pdf' ? 'PDF' : 'XML'})`} sx={{ overflowWrap: 'anywhere' }} />
                                    <KitKV label="Total" value={inv.total ? `R$ ${inv.total}` : '—'} />
                                </KitCard>

                                {inv.source === 'pdf' && (
                                    <Box sx={{ mt: '10px' }}>
                                        <KitBanner tone="info" icon={InfoIcon} iconSize={18}>
                                            Dados extraídos do PDF por inteligência artificial: confira códigos, quantidades e valores antes de lançar.
                                        </KitBanner>
                                    </Box>
                                )}
                                {inv.result && (
                                    <Box sx={{ mt: '10px' }}>
                                        <KitBanner tone={inv.result.status === 'ok' ? 'success' : inv.result.status === 'partial' ? undefined : 'danger'}>
                                            {inv.result.message
                                                || `${inv.result.updated} produto(s) atualizado(s), ${inv.result.created} criado(s)${inv.result.supplier_created ? ', fornecedor cadastrado' : ''}.`}
                                        </KitBanner>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '14px 15px 8px' }}>
                                    <Check
                                        label="Selecionar todos os itens da nota"
                                        disabled={!selectable.length}
                                        checked={selectable.length > 0 && sel === selectable.length}
                                        indeterminate={sel > 0 && sel < selectable.length}
                                        onChange={(e) => onToggleInvoice(inv.uid, e.target.checked)}
                                    />
                                    <Box sx={{ fontSize: 12, color: 'text.disabled', fontWeight: 600 }}>Itens ({inv.items.length})</Box>
                                </Box>
                                <KitList>
                                    {inv.items.map((it) => {
                                        const locked = it.done || it.already_imported;
                                        return (
                                            <Box key={it.key} sx={{ p: '13px 14px', borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 }, backgroundColor: it.selected ? 'info.bg' : 'transparent' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                                    <Check
                                                        label={`Selecionar ${it.name}`}
                                                        disabled={!isSelectable(it)}
                                                        checked={it.selected}
                                                        onChange={(e) => onUpdateItem(inv.uid, it.key, { selected: e.target.checked })}
                                                    />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Box sx={{ fontWeight: 700, fontSize: 14, overflowWrap: 'anywhere' }}>{it.name}</Box>
                                                        <Box sx={{ fontSize: 12, color: 'text.disabled', mt: '1px' }}>
                                                            {it.code ? `Cód. ${it.code} · ` : ''}{it.quantity}{it.unit ? ` ${it.unit}` : ''} × {it.unit_cost}
                                                        </Box>
                                                        {it.warning && <Box sx={{ fontSize: 11.5, color: 'warning.main', mt: '2px' }}>{it.warning}</Box>}
                                                    </Box>
                                                    <Box sx={{ fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{it.total}</Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', mt: '10px', pl: '34px' }}>
                                                    <Box
                                                        component="button"
                                                        type="button"
                                                        disabled={locked}
                                                        onClick={() => { setPickerQuery(''); setPickerFor({ uid: inv.uid, key: it.key }); }}
                                                        sx={{
                                                            width: '100%', minWidth: 0, height: 44, borderRadius: '12px', border: '1.5px solid', borderColor: 'divider', backgroundColor: 'background.paper',
                                                            p: '0 12px', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'inherit', fontSize: 13, textAlign: 'left',
                                                            color: it.linked ? 'text.primary' : 'text.disabled', cursor: 'pointer', '&:disabled': { opacity: 0.6, cursor: 'default' },
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', flexShrink: 0 }}><PackageIcon size={16} /></Box>
                                                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.linked ? productLabel(it.linked) : 'Novo produto (sem vínculo)'}</Box>
                                                    </Box>
                                                    <ItemStatus item={it} />
                                                </Box>
                                                {!it.done && !it.already_imported && it.result && (
                                                    <Box sx={{ fontSize: 11.5, color: 'error.main', mt: '6px', pl: '34px' }}>{it.result}</Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </KitList>
                            </Box>
                        );
                    })}

                    <SecH title="Enviar mais notas" />
                    <KitButton variant="soft" block onClick={() => fileInputRef.current?.click()}>
                        <UploadIcon size={18} /> Escolher arquivos
                    </KitButton>
                    {fileInput}
                    {fileList}
                    {files.length > 0 && (
                        <KitButton block sx={{ mt: '12px' }} disabled={parsing} onClick={onParse}>
                            {parsing ? 'Lendo notas...' : `Ler notas (${files.length})`}
                        </KitButton>
                    )}

                    <KitFormBar>
                        <KitButton disabled={submitting || !selectedCount} onClick={onCreate}>Criar produto ({creatableCount})</KitButton>
                        <KitButton variant="primary" disabled={submitting || !selectedCount} onClick={onUpdate}>
                            {submitting ? 'Lançando...' : `Atualizar estoque (${updatableCount})`}
                        </KitButton>
                    </KitFormBar>
                </>
            )}

            <MobileSheet open={Boolean(pickerFor)} onClose={() => setPickerFor(null)} title="Produto no sistema">
                <Box sx={{ display: 'flex', mb: '12px' }}>
                    <KitSearch value={pickerQuery} onChange={(e) => setPickerQuery(e.target.value)} placeholder="Nome ou código…" inputProps={{ 'aria-label': 'Buscar produto' }} />
                </Box>
                <KitList>
                    <Box component="button" type="button" onClick={() => pickProduct(null)} sx={pickRowSx}>
                        <Box sx={{ fontWeight: 700, color: 'text.disabled' }}>Novo produto (sem vínculo)</Box>
                    </Box>
                    {pickerResults.map((p) => (
                        <Box key={p.id} component="button" type="button" onClick={() => pickProduct(p)} sx={pickRowSx}>
                            <Box sx={{ fontWeight: 700, fontSize: 14 }}>{p.name}</Box>
                            {p.code && <Box sx={{ fontSize: 12, color: 'text.disabled' }}>Cód. {p.code}</Box>}
                        </Box>
                    ))}
                    {pickerResults.length === 0 && <Box sx={{ p: '16px', fontSize: 13, color: 'text.disabled' }}>Nenhum produto encontrado</Box>}
                </KitList>
            </MobileSheet>
        </Box>
    );
}

const pickRowSx = {
    display: 'block', width: '100%', minHeight: 52, p: '10px 14px', textAlign: 'left', border: 0, borderBottom: '1px solid', borderColor: 'divider',
    background: 'none', fontFamily: 'inherit', color: 'text.primary', cursor: 'pointer', '&:last-child': { borderBottom: 0 }, '&:active': { backgroundColor: 'background.subtle' },
};
